import React, { useEffect, useState } from "react";
import api from "../../api";
import { Loader2, Plus, Trash2, Pencil, X, Sparkles } from "lucide-react";
import { toast } from "../../hooks/use-toast";
import ImageUpload from "../../components/ImageUpload";
import { usePlan } from "../../context/PlanContext";

const KindPicker = ({ modal, setModal }) => {
  const { has } = usePlan();
  const allowed = has("experience_vouchers") || modal.kind === "experience";
  const exp = modal.kind === "experience";
  return (
    <div data-testid="product-kind-picker">
      <label className="block text-sm font-medium text-[#16243b] mb-1.5">Voucher type</label>
      <div className="grid grid-cols-2 gap-2">
        {[["money", "Money value", "Spend any amount until it runs out"], ["experience", "Experience", "Used once, e.g. afternoon tea for two"]].map(([k, l, h]) => (
          <button type="button" key={k} disabled={k === "experience" && !allowed} onClick={() => setModal({ ...modal, kind: k })} data-testid={`product-kind-${k}`}
            className={`text-left rounded-xl border p-3 transition-colors disabled:opacity-40 ${(modal.kind || "money") === k ? "border-[#2ec9c9] bg-[#eefafa]" : "border-gray-200"}`}>
            <span className="block text-sm font-semibold text-[#16243b]">{l}</span><span className="block text-xs text-[#8a97a8]">{h}</span>
          </button>
        ))}
      </div>
      {!allowed && <p className="text-xs text-[#8a97a8] mt-1">Experience vouchers are on the Standard and Enhanced plans.</p>}
      {exp && <textarea rows={2} value={modal.experience_details || ""} onChange={(e) => setModal({ ...modal, experience_details: e.target.value })} data-testid="product-experience-details"
        placeholder="What's included, e.g. Sandwiches, scones and a glass of prosecco for two" className="mt-2 w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm outline-none focus:border-[#2ec9c9]" />}
    </div>
  );
};

const empty = { title: "", description: "", long_description: "", terms: "", price: "", image_url: "", gallery_images: [], active: true, seo_title: "", seo_description: "", share_image: "", kind: "money", experience_details: "" };

export default function ProductsTab() {
  const [products, setProducts] = useState(null);
  const [modal, setModal] = useState(null); // product being edited or empty

  const load = () => api.get("/business/products").then(({ data }) => setProducts(data));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...modal, price: parseFloat(modal.price) || 0 };
    try {
      if (modal.id) await api.put(`/business/products/${modal.id}`, payload);
      else await api.post("/business/products", payload);
      toast({ title: "Saved", description: "Voucher product saved." });
      setModal(null); load();
    } catch (err) {
      toast({ title: "Error", description: err?.response?.data?.detail || "Failed to save", variant: "destructive" });
    }
  };

  const del = async (id) => {
    await api.delete(`/business/products/${id}`);
    toast({ title: "Deleted" }); load();
  };

  const seedDefaults = async () => {
    try {
      const { data } = await api.post("/business/products/seed-defaults");
      toast({ title: data.created ? "Starter pack added" : "Nothing to add", description: data.created ? `${data.created} starter voucher(s) added.` : "You already have vouchers at those amounts." });
      load();
    } catch {
      toast({ title: "Error", description: "Could not add starter pack", variant: "destructive" });
    }
  };

  if (!products) return <Loader2 className="animate-spin text-[#2ec9c9]" size={32} />;

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <button onClick={() => setModal({ ...empty })} data-testid="add-voucher-btn" className="gp-btn-teal rounded-full px-5 py-2.5 text-sm font-medium flex items-center gap-2">
          <Plus size={18} /> Add voucher
        </button>
        <button onClick={seedDefaults} data-testid="seed-defaults-btn" className="gp-btn-outline rounded-full px-5 py-2.5 text-sm font-medium flex items-center gap-2">
          <Sparkles size={16} /> Add starter voucher pack
        </button>
      </div>

      {products.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 text-center text-[#8a97a8] shadow-[0_8px_24px_rgba(20,30,55,0.06)]">
          No voucher products yet. Add your first to start selling.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {products.map((p) => (
            <div key={p.id} className="bg-white rounded-2xl overflow-hidden shadow-[0_8px_24px_rgba(20,30,55,0.06)]">
              <div className="aspect-square bg-gray-100">{p.image_url && <img src={p.image_url} alt={p.title} className="w-full h-full object-cover" />}</div>
              <div className="p-4">
                <div className="flex justify-between items-start">
                  <h3 className="font-semibold text-[#16243b]">{p.title}{p.kind === "experience" && <span className="ml-2 text-[10px] font-semibold uppercase tracking-wide bg-[#eefafa] text-[#1fb0b0] px-2 py-0.5 rounded-full align-middle" data-testid={`product-experience-badge-${p.id}`}>Experience</span>}</h3>
                  <span className="font-bold text-[#2ec9c9]">£{p.price.toFixed(2)}</span>
                </div>
                <p className="text-sm text-[#8a97a8] mt-1 line-clamp-2">{p.description}</p>
                <div className="flex gap-2 mt-4">
                  <button onClick={() => setModal({ ...p, price: String(p.price) })} className="flex-1 gp-btn-outline rounded-lg py-2 text-sm font-medium flex items-center justify-center gap-1"><Pencil size={14} /> Edit</button>
                  <button onClick={() => del(p.id)} className="px-3 rounded-lg border border-red-200 text-red-500 hover:bg-red-50 transition-colors"><Trash2 size={16} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {modal && (
        <div className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-4" onClick={() => setModal(null)}>
          <div className="bg-white rounded-2xl w-full max-w-[440px] max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center p-5 border-b border-gray-100">
              <h3 className="font-bold text-[#16243b]">{modal.id ? "Edit voucher" : "New voucher"}</h3>
              <button onClick={() => setModal(null)}><X className="text-[#5a6b80]" /></button>
            </div>
            <form onSubmit={save} className="p-5 space-y-4">
              <Field label="Title" value={modal.title} onChange={(v) => setModal({ ...modal, title: v })} required />
              <KindPicker modal={modal} setModal={setModal} />
              <Field label="Short description" value={modal.description} onChange={(v) => setModal({ ...modal, description: v })} textarea />
              <Field label="Full description (voucher page)" value={modal.long_description} onChange={(v) => setModal({ ...modal, long_description: v })} textarea />
              <Field label="Price (£)" value={modal.price} onChange={(v) => setModal({ ...modal, price: v })} type="number" required />
              <ImageUpload label="Main image" square hint="Use a square (1:1) image — e.g. 1000×1000px. Non-square images are auto-cropped to a centred square." value={modal.image_url} onChange={(v) => setModal({ ...modal, image_url: v })} />
              <div>
                <label className="block text-sm font-medium text-[#16243b] mb-1.5">Gallery images</label>
                <p className="text-xs text-[#8a97a8] mb-2 -mt-0.5">Square (1:1) images work best — uploads are auto-cropped to square.</p>
                <div className="space-y-2">
                  {(modal.gallery_images || []).map((g, i) => (
                    <ImageUpload key={i} value={g} compact square
                      onChange={(v) => setModal({ ...modal, gallery_images: modal.gallery_images.map((x, xi) => (xi === i ? v : x)) })} />
                  ))}
                  <button type="button" onClick={() => setModal({ ...modal, gallery_images: [...(modal.gallery_images || []), ""] })}
                    className="text-[#1fb0b0] text-sm font-medium flex items-center gap-1"><Plus size={14} /> Add gallery image</button>
                </div>
              </div>
              <Field label="Terms & conditions (voucher page)" value={modal.terms} onChange={(v) => setModal({ ...modal, terms: v })} textarea />
              <div className="border-t border-gray-100 pt-4">
                <h4 className="font-semibold text-[#16243b] text-sm mb-1">SEO & social sharing</h4>
                <p className="text-xs text-[#8a97a8] mb-3">Sets this voucher page's title, search description and link-preview image.</p>
                <div className="space-y-4">
                  <Field label="SEO page title" value={modal.seo_title} onChange={(v) => setModal({ ...modal, seo_title: v })} placeholder={modal.title} testid="product-seo-title" />
                  <Field label="SEO / share description" value={modal.seo_description} onChange={(v) => setModal({ ...modal, seo_description: v })} textarea placeholder={modal.description} testid="product-seo-description" />
                  <ImageUpload label="Social share image" value={modal.share_image} onChange={(v) => setModal({ ...modal, share_image: v })} />
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm text-[#16243b]">
                <input type="checkbox" checked={modal.active} onChange={(e) => setModal({ ...modal, active: e.target.checked })} /> Active (visible in store)
              </label>
              <button className="gp-btn-teal w-full rounded-xl py-3 font-medium">Save voucher</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, type = "text", textarea, required, placeholder, testid }) {
  return (
    <div>
      <label className="block text-sm font-medium text-[#16243b] mb-1.5">{label}</label>
      {textarea ? (
        <textarea value={value ?? ""} onChange={(e) => onChange(e.target.value)} rows={2} data-testid={testid} placeholder={placeholder}
          className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9] focus:ring-2 focus:ring-[#2ec9c9]/20 resize-none" />
      ) : (
        <input type={type} step="0.01" value={value ?? ""} required={required} placeholder={placeholder} data-testid={testid}
          onChange={(e) => onChange(e.target.value)}
          className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9] focus:ring-2 focus:ring-[#2ec9c9]/20" />
      )}
    </div>
  );
}
